import { io, Socket } from "socket.io-client"
import { MINE_SOCKETS } from "store/apis/mineGameAPI/values.ts"
import { missionsApi } from "store/apis/missionsAPI/missions.api"
import { balanceEndpoints } from "store/apis/balanceAPI/balance.api"
import { IUserBalance, INewBalance } from "store/apis/balanceAPI/balance.types"
import { getSocketsUrl } from "utils/getSocketsUrl.ts"
import { TAGS, api } from "../../api.ts"

// Кількість активних change-next-action запитів. Поки > 0 — серверний nextAction
// із сокета НЕ застосовуємо, щоб не відкочувати оптимістичне значення гравця.
let pendingNextActionChanges = 0

export const socketMine = {
  instance: null as Socket | null,
  updateUserProgressData: null as
    | ((updater: (draft: any) => void) => void)
    | null,
  updateMineData: null as ((updater: (draft: any) => void) => void) | null,

  _subs: 0,
  _boundEvents: new Set<string>(),

  init() {
    if (this.instance) return

    this.instance = io(`${getSocketsUrl()}/mine`, {
      auth: {
        userId: localStorage.getItem("userId")
      },
      transports: ["websocket"]
    })
  },

  setUpdateUserProgressData(
    updateCachedData: (updater: (draft: any) => void) => void
  ) {
    this.updateUserProgressData = updateCachedData
  },

  setUpdateMineData(updateCachedData: (updater: (draft: any) => void) => void) {
    this.updateMineData = updateCachedData
  },

  addSub() {
    this._subs += 1
  },

  removeSub() {
    this._subs = Math.max(0, this._subs - 1)
    if (this._subs === 0) {
      try {
        if (this.instance) {
          this._boundEvents.forEach((evt) =>
            this.instance!.removeAllListeners(evt)
          )
        }
        this._boundEvents.clear()
        this.instance?.disconnect()
      } finally {
        this.instance = null
      }
    }
  },

  _bind(eventName: string, handler: (payload: any) => void) {
    if (!this.instance) return () => {}
    if (this._boundEvents.has(eventName)) return () => {}

    this.instance.on(eventName, handler)
    this._boundEvents.add(eventName)

    return () => {
      try {
        this.instance?.off(eventName, handler)
      } finally {
        this._boundEvents.delete(eventName)
      }
    }
  },

  bindMineUpdateListener() {
    const handler = (event: any) => {
      const {
        health,
        activePlayers,
        allTimeLabrFarmed,
        todayLabrFarmed,
        sinceLastRecoveryLabrFarmed,
        reclaimAt,
        brokeAt
      } = JSON.parse(event)

      this.updateMineData?.((draft) => {
        draft.health = health
        draft.activePlayers = activePlayers
        draft.allTimeLabrFarmed = allTimeLabrFarmed
        draft.todayLabrFarmed = todayLabrFarmed
        draft.sinceLastRecoveryLabrFarmed = sinceLastRecoveryLabrFarmed
        draft.reclaimAt = reclaimAt
        draft.brokeAt = brokeAt
      })
    }
    return this._bind(MINE_SOCKETS.DATA, handler)
  },

  bindProgressUpdateListener() {
    const handler = (event: any) => {
      const { progress } = event
      if (!progress) return

      this.updateUserProgressData?.((draft) => {
        // nextAction — намір гравця (оптимістичний). Серверний снапшот застосовуємо
        // лише коли немає змін у польоті (жива корекція дрейфу); під час активного
        // перемикання захищаємо оптимістичне значення (немає відскоку).
        // null/undefined-гард не дає сокету занулити перемикач, якщо сервер
        // присилає nextAction як null (default) або взагалі без поля.
        const rest = { ...progress }
        if (pendingNextActionChanges > 0 || rest.nextAction == null) {
          delete rest.nextAction
        }
        Object.assign(draft, rest)
      })
    }
    return this._bind(MINE_SOCKETS.REWARD, handler)
  }
}

export const mineGameAPI = api.enhanceEndpoints({
  addTagTypes: [TAGS.MINE_GAME, TAGS.USER, TAGS.MISSIONS]
})

const mineGameEndpoints = mineGameAPI.injectEndpoints({
  endpoints: (builder) => ({
    getMineInfo: builder.query<MineInfo, void>({
      query: () => ({
        url: `/mine-game`,
        method: "GET"
      }),
      providesTags: [{ type: TAGS.MINE_GAME, id: `MINE INFO` }],
      async onCacheEntryAdded(_, { updateCachedData, cacheEntryRemoved }) {
        socketMine.setUpdateMineData(updateCachedData)

        socketMine.init()
        socketMine.addSub()
        const unsubscribeMineUpdate = socketMine.bindMineUpdateListener()

        try {
          await cacheEntryRemoved
        } finally {
          unsubscribeMineUpdate?.()
          socketMine.removeSub()
        }
      }
    }),
    enterMineGame: builder.mutation<{ newBalance: IUserBalance }, void>({
      query: () => ({
        url: `/mine-game/enter`,
        method: "PATCH"
      }),
      invalidatesTags: [
        { type: TAGS.MINE_GAME, id: "USER MINE GAME PROGRESS" },
        { type: TAGS.MINE_GAME, id: "USER MINE GAME STATISTICS" }
      ],
      onQueryStarted: async (_, { queryFulfilled, dispatch }) => {
        const { data } = await queryFulfilled

        dispatch(
          balanceEndpoints.util.updateQueryData(
            "getBalance",
            undefined,
            () => data.newBalance
          )
        )
      }
    }),
    getEarlyLeavePrice: builder.query<
      { price: number; currency: string; topTool: string },
      void
    >({
      query: () => ({
        url: `/mine-game/early-leave-price`,
        method: "GET"
      })
    }),
    leaveMineGame: builder.mutation<{ newBalance: IUserBalance }, void>({
      query: () => ({
        url: `/mine-game/leave`,
        method: "PATCH"
      }),
      invalidatesTags: [
        { type: TAGS.MINE_GAME, id: "USER MINE GAME PROGRESS" },
        { type: TAGS.MINE_GAME, id: "USER MINE GAME STATISTICS" }
      ],
      onQueryStarted: async (_, { queryFulfilled, dispatch }) => {
        const { data } = await queryFulfilled

        dispatch(
          balanceEndpoints.util.updateQueryData(
            "getBalance",
            undefined,
            () => data.newBalance
          )
        )
      }
    }),
    checkIsInMineGame: builder.mutation<any, void>({
      query: () => ({
        url: `/mine-game/is-in-game`,
        method: "PATCH"
      })
    }),
    claimMineGameReward: builder.mutation<{ newBalance: IUserBalance }, void>({
      query: () => ({
        url: `/mine-game/claim`,
        method: "PATCH"
      }),
      invalidatesTags: [
        { type: TAGS.MINE_GAME, id: "USER MINE GAME PROGRESS" },
        { type: TAGS.MINE_GAME, id: "USER MINE GAME STATISTICS" },
        { type: TAGS.USER, id: "BALANCE_HISTORY" }
      ],
      onQueryStarted: async (_, { queryFulfilled, dispatch }) => {
        const { data } = await queryFulfilled

        dispatch(
          balanceEndpoints.util.updateQueryData(
            "getBalance",
            undefined,
            () => data.newBalance
          )
        )

        dispatch(
          missionsApi.util.invalidateTags([
            { type: TAGS.MISSIONS, id: "MY_PROGRESS" },
            { type: TAGS.MISSIONS, id: "HAS_UNCLAIMED" }
          ])
        )
      }
    }),
    makeFirstChoiceMineGame: builder.mutation<void, string>({
      query: (action) => ({
        url: `/mine-game/first-choice/${action}`,
        method: "PATCH"
      }),
      invalidatesTags: [
        { type: TAGS.MINE_GAME, id: "USER MINE GAME PROGRESS" },
        { type: TAGS.MINE_GAME, id: "USER MINE GAME STATISTICS" }
      ]
    }),
    changeNextActionMineGame: builder.mutation<void, string>({
      query: (action) => ({
        url: `/mine-game/change-next-action/${action}`,
        method: "PATCH"
      }),
      async onQueryStarted(action, { dispatch, queryFulfilled }) {
        pendingNextActionChanges++

        const patchResult = dispatch(
          mineGameAPI.util.updateQueryData(
            "getUserProgressMineGame" as never,
            undefined as never,
            (draft) => {
              ;(draft as unknown as IMineGameProgress).nextAction = action
            }
          )
        )

        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        } finally {
          pendingNextActionChanges--
        }
      }
    }),
    digUpMineGame: builder.mutation<{ newBalance: INewBalance }, void>({
      query: () => ({
        url: `/mine-game/speed-up`,
        method: "PATCH"
      }),
      invalidatesTags: [
        { type: TAGS.MINE_GAME, id: "USER MINE GAME PROGRESS" }
      ],
      onQueryStarted: async (_, { queryFulfilled, dispatch }) => {
        const { data } = await queryFulfilled

        dispatch(
          balanceEndpoints.util.updateQueryData(
            "getBalance",
            undefined,
            () => data.newBalance
          )
        )
      }
    }),
    getUserProgressMineGame: builder.query<IMineGameProgress, void>({
      query: () => ({
        url: "mine-game/progress",
        method: "GET"
      }),
      providesTags: [{ type: TAGS.MINE_GAME, id: `USER MINE GAME PROGRESS` }],
      async onCacheEntryAdded(_, { updateCachedData, cacheEntryRemoved }) {
        socketMine.setUpdateUserProgressData(updateCachedData)

        socketMine.init()
        socketMine.addSub()
        const unsubscribeProgressUpdate =
          socketMine.bindProgressUpdateListener()

        try {
          await cacheEntryRemoved
        } finally {
          unsubscribeProgressUpdate?.()
          socketMine.removeSub()
        }
      }
    }),
    getUserStatisticsMineGame: builder.query<any, void>({
      query: () => ({
        url: "mine-game/progress-statistics",
        method: "GET"
      }),
      providesTags: [{ type: TAGS.MINE_GAME, id: `USER MINE GAME STATISTICS` }]
    })
  })
})

export const {
  useGetMineInfoQuery,
  useEnterMineGameMutation,
  useGetEarlyLeavePriceQuery,
  useLeaveMineGameMutation,
  useClaimMineGameRewardMutation,
  useMakeFirstChoiceMineGameMutation,
  useChangeNextActionMineGameMutation,
  useDigUpMineGameMutation,
  useGetUserProgressMineGameQuery,
  useGetUserStatisticsMineGameQuery,
  endpoints: { getUserProgressMineGame }
} = mineGameEndpoints
